import React, { useMemo, useState, useEffect } from "react";
import {
    HiOutlineBriefcase,
    HiOutlineBuildingOffice2,
    HiOutlineTrash,
} from "react-icons/hi2";
import "../styles/FavoritesTabs.css";

function FavoritesTabs({
    activeTab = "jobs", // 'jobs' or 'companies'
    setActiveTab = () => {},
    favoriteCompanyIds = [],
    favoriteCompanyInfo = [],
    onRemoveFavoriteCompany = () => {},
    onLoadFavoriteCompanyJobs = async () => {}, // parent fetch function
    user = {},
}) {
    const [companyJobsLoading, setCompanyJobsLoading] = useState(false);
    const [selectedCompanyId, setSelectedCompanyId] = useState(() => {
        return localStorage.getItem("selectedCompanyId") || null;
    });

    const MAX_FREE_FAVORITE_COMPANIES = 3;
    const isPremiumUser = user?.plan === "premium";

    const handleCompaniesTabClick = () => {
        // Only switch tabs. Do NOT auto-load anything here.
        setActiveTab("companies");
    };

    useEffect(() => {
        if (selectedCompanyId) {
            localStorage.setItem("selectedCompanyId", selectedCompanyId);
        } else {
            localStorage.removeItem("selectedCompanyId");
        }
    }, [selectedCompanyId]);

    useEffect(() => {
        if (
            selectedCompanyId &&
            !favoriteCompanyIds.includes(selectedCompanyId)
        ) {
            setSelectedCompanyId(null);
        }
    }, [favoriteCompanyIds, selectedCompanyId]);

    const handleCompanyClick = async (companyId) => {
        if (!companyId || companyJobsLoading) return;

        setSelectedCompanyId(companyId);
        setCompanyJobsLoading(true);

        try {
            await onLoadFavoriteCompanyJobs(companyId);
        } catch (err) {
            // parent handles errors
        } finally {
            setCompanyJobsLoading(false);
        }
    };

    const companyCount = favoriteCompanyIds.length;

    const showFavoriteCompanyLimitMessage =
        activeTab === "companies" &&
        !isPremiumUser &&
        companyCount >= MAX_FREE_FAVORITE_COMPANIES;

    const favoriteCompanyIdsSet = useMemo(() => {
        return new Set((favoriteCompanyIds || []).map(String));
    }, [favoriteCompanyIds]);

    const normalizedFavoriteCompanyInfo = useMemo(() => {
        return (favoriteCompanyInfo || []).filter((company) =>
            favoriteCompanyIdsSet.has(String(company.id))
        );
    }, [favoriteCompanyInfo, favoriteCompanyIdsSet]);

    return (
        <div className="favorites-tabs-wrapper">
            <div className="favorites-tabs-nav">
                <button
                    className={`favorites-tab-btn ${
                        activeTab === "jobs" ? "is-active" : ""
                    }`}
                    onClick={() => setActiveTab("jobs")}
                >
                    <HiOutlineBriefcase size={18} />
                    <span>Jobs</span>
                    <span className="favorites-tab-count">
                        {user?.favorites?.length || 0}
                    </span>
                </button>

                <button
                    className={`favorites-tab-btn ${
                        activeTab === "companies" ? "is-active" : ""
                    }`}
                    onClick={handleCompaniesTabClick}
                >
                    <HiOutlineBuildingOffice2 size={18} />
                    <span>Companies</span>
                    <span className="favorites-tab-count">{companyCount}</span>
                </button>
            </div>

            {activeTab === "companies" && (
                <div className="favorites-companies-content">
                    {normalizedFavoriteCompanyInfo.length === 0 ? (
                        <div className="favorites-companies-empty">
                            <HiOutlineBuildingOffice2 size={40} />
                            <h3>No favorite companies yet</h3>
                            <p>
                                Use the company search on the Discover tab to find and
                                favorite companies you're interested in.
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="favorites-companies-grid">
                                {normalizedFavoriteCompanyInfo.map((company, idx) => {
                                    const isSelected =
                                        String(selectedCompanyId) === String(company.id);

                                    return (
                                        <div
                                            key={company.id ?? `${company.name}-${idx}`}
                                            className={`favorites-company-card ${
                                                isSelected ? "is-selected" : ""
                                            }`}
                                            onClick={() => handleCompanyClick(company.id)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    handleCompanyClick(company.id);
                                                }
                                            }}
                                        >
                                            <div className="favorites-company-header">
                                                <CompanyLogo company={company} />

                                                <div className="favorites-company-info">
                                                    <h4 className="favorites-company-name">
                                                        {company.name}
                                                    </h4>
                                                    {company.industry && (
                                                        <span className="favorites-company-industry">
                                                            {company.industry}
                                                        </span>
                                                    )}
                                                </div>

                                                <button
                                                    className="favorites-company-remove"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onRemoveFavoriteCompany(company.id, true);
                                                    }}
                                                    title="Remove from favorites"
                                                    disabled={companyJobsLoading}
                                                >
                                                    <HiOutlineTrash size={16} />
                                                </button>
                                            </div>

                                            <div className="favorites-company-stats">
                                                <div className="favorites-company-stat">
                                                    <span className="stat-value">
                                                        {company.activePositionCount ?? 0}
                                                    </span>
                                                    <span className="stat-label">
                                                        Active Position
                                                        {(company.activePositionCount ?? 0) !== 1
                                                            ? "s"
                                                            : ""}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {showFavoriteCompanyLimitMessage && (
                                <div className="favorites-upgrade-message">
                                    You’ve reached the free plan limit of{" "}
                                    {MAX_FREE_FAVORITE_COMPANIES} favorite companies.
                                    Upgrade your plan to save more companies.
                                </div>
                            )}

                            {selectedCompanyId === null && (
                                <div
                                    className="favorites-companies-empty"
                                    style={{ marginTop: "16px" }}
                                >
                                    <HiOutlineBriefcase size={40} />
                                    <h3>View Company Postings</h3>
                                    <p>Select a company above to view available positions.</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {activeTab === "jobs" && (
                <div className="favorites-companies-content">
                    {user.favorites.length === 0 && (
                        <div className="favorites-companies-empty">
                            <HiOutlineBriefcase size={40} />
                            <h3>No favorite jobs yet</h3>
                            <p>
                                Use the star icon on the Discover tab to favorite jobs you're interested in, and they'll show up here.
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function CompanyLogo({ company }) {
    const [logoError, setLogoError] = useState(false);

    const letter = (company?.name || "?").charAt(0).toUpperCase();

    if (company?.logo && !logoError) {
        return (
            <img
                src={company.logo}
                alt={`${company.name} logo`}
                className="favorites-company-logo"
                onError={() => setLogoError(true)}
            />
        );
    }

    return (
        <div className="favorites-company-logo-fallback">
            {letter}
        </div>
    );
}

export { FavoritesTabs };